import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AppConfig } from 'config';

import { PaymentStates } from 'stores/Payment';
import { useMst } from 'stores/RootStore';

import { Amplitude, ANALYTICAL_EVENTS } from 'services/amplitude';

import { useStripePaywall } from 'hooks/useStripePaywall';

import { DISABLED_BUTTONS_DATA, PaymentTypes } from 'constants/common';

import { getStripeButtonText } from 'pages/PaywallPage/utils';

import { Button } from 'components/Button/Button';

import { FormInputs } from './FormInputs/FormInputs';

import {
  DisabledButton,
  DisabledButtonWrap,
  FormContainer,
  FormContainerBottomBlock,
  FormContainerTopBlock,
  FormContainerTopText,
  SubmitButtonWrap,
} from './StripeFormContainer.style';

const PaymentRequestButtonElementStyled = styled(PaymentRequestButtonElement)`
  width: 100%;
  padding: 16px;
`;

interface IStripeFormContainer {
  cardLayout: boolean;
  buttonText?: string;
  handlePaymentSuccess: () => void;
  handlePaymentError: () => void;
}

export const StripeFormContainer = observer(
  ({
    cardLayout,
    buttonText,
    handlePaymentSuccess,
    handlePaymentError,
  }: IStripeFormContainer) => {
    const paywallElements = useStripePaywall();
    const {
      paymentStore,
      authStore: { email },
    } = useMst();

    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
      null,
    );

    const product = paymentStore.stripeProductData;

    useEffect(() => {
      if (stripe) {
        const pr = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Senses Subscription',
            amount: product?.amount ?? 0,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        // Check the availability of the Payment Request API.
        pr.canMakePayment().then(result => {
          if (result) {
            setPaymentRequest(pr);
          }
        });

        pr.on('paymentmethod', async ev => {
          // Confirm the PaymentIntent without handling potential next actions (yet).

          stripe.retrieveSetupIntent;

          const { paymentIntent, error: confirmError } =
            await stripe.confirmCardPayment(
              AppConfig.STRIPE_PUBLISHABLE_KEY ?? '',
              { payment_method: ev.paymentMethod.id },
              { handleActions: false },
            );

          if (confirmError) {
            console.log('error confirming payment');
            paymentStore.resetPaymentState();
            ev.complete('fail');
          } else {
            ev.complete('success');

            if (paymentIntent?.status === 'requires_action') {
              const { error } = await stripe.confirmCardPayment(
                AppConfig.STRIPE_PUBLISHABLE_KEY ?? '',
              );
              if (error) {
                console.log('error confirming card payment');
                paymentStore.resetPaymentState();
              } else {
                paymentStore.setPaymentApproved();
              }
            } else {
              paymentStore.setPaymentApproved();
            }
          }
        });
      }
    }, [stripe]);

    const buttonContent = buttonText || getStripeButtonText(product);

    const isInputDisabled =
      paymentStore.paymentState === PaymentStates.stripeLoading;

    const onClickPay = () => {
      if (paywallElements.inputError) {
        // TODO, если сразу нажать continue, то не покажутся ошибки в инпутах, а сразу покажется окно try again
        return;
      }
      Amplitude.logEvent(ANALYTICAL_EVENTS.PAYWALL_TRY_SUBSCRIBE, {
        source: PaymentTypes.STRIPE,
      });
      const addressZip = paywallElements.addressZipProps.value;
      paymentStore.createCardPaySubscription({
        cardPay: { cardData: 'CardNumberElement', addressZip },
        userInfo: { email },
        onSuccess: handlePaymentSuccess,
        onError: handlePaymentError,
      });
    };

    console.log(paymentRequest);

    return (
      <>
        <FormContainer cardLayout={cardLayout}>
          <FormContainerTopBlock cardLayout={cardLayout}>
            <FormContainerTopText>Payment Info</FormContainerTopText>
          </FormContainerTopBlock>
          <FormContainerBottomBlock cardLayout={cardLayout}>
            {cardLayout && (
              <DisabledButtonWrap cardLayout={cardLayout}>
                {DISABLED_BUTTONS_DATA.map(({ src }) => (
                  <DisabledButton key={src} size="small" disabled>
                    <img src={src} key={src} />
                  </DisabledButton>
                ))}
              </DisabledButtonWrap>
            )}
            <FormInputs {...paywallElements} disabled={isInputDisabled} />
          </FormContainerBottomBlock>
          {!cardLayout && (
            <DisabledButtonWrap cardLayout={cardLayout}>
              {DISABLED_BUTTONS_DATA.map(({ src }) => (
                <DisabledButton key={src} size="small" disabled>
                  <img src={src} key={src} />
                </DisabledButton>
              ))}
            </DisabledButtonWrap>
          )}
        </FormContainer>
        {!!paymentRequest && (
          <PaymentRequestButtonElementStyled options={{ paymentRequest }} />
        )}
        <SubmitButtonWrap>
          <Button loading={paymentStore.isLoading} onClick={onClickPay}>
            {buttonContent}
          </Button>
        </SubmitButtonWrap>
      </>
    );
  },
);
